import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, OnDestroy, Optional } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { debounceTime, startWith, takeUntil } from 'rxjs/operators';
import { FormTemplateService } from '../../api/api/formTemplate.service';
import { AdminService } from '../../api/api/admin.service';
import { FormJson } from '../../api/model/formJson';
import { FormTemplateDto } from '../../api/model/formTemplateDto';
import { OrganisationSummaryDto } from '../../api/model/organisationSummaryDto';
import { extractApiErrorMessage } from '../../shared/utils/api-error';
import { CreateTemplateDialogData, CreateTemplateResult, createEmptyAuditFormJson, parseFormJsonInput } from './create-template-form';

interface PlanOption {
  value: string;
  label: string;
  id: number;
}

/** Woher das Formular der neuen Vorlage kommt (DOCU-1358). */
export type FormSource = 'empty' | 'json';

/**
 * DOCU-1358: Stammdaten abfragen, Formular leer anlegen (danach Designer)
 * oder aus JSON übernehmen. Kein `entryFormJson` mehr (ADR 0001).
 */
@Component({
  selector: 'app-create-template-dialog',
  templateUrl: './create-template-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateTemplateDialogComponent implements OnInit, OnDestroy {
  readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, notBlank] }),
    description: new FormControl('', { nonNullable: true }),
    catalogKey: new FormControl('', { nonNullable: true }),
    minimalPlanKey: new FormControl('', { nonNullable: true }),
    source: new FormControl<FormSource>('empty', { nonNullable: true }),
    json: new FormControl('', { nonNullable: true }),
  });

  saving = false;
  saveError: string | null = null;
  loadedFileName: string | null = null;

  // Org selector
  orgSearchControl = new FormControl<OrganisationSummaryDto | string>('');
  selectedOrg: OrganisationSummaryDto | null = null;
  allOrgs: OrganisationSummaryDto[] = [];
  filteredOrgs: OrganisationSummaryDto[] = [];
  orgsLoading = false;

  private destroy$ = new Subject<void>();

  readonly planOptions: PlanOption[] = [
    { value: 'FREE',       label: 'Free',       id: 1 },
    { value: 'BASIC',      label: 'Basic',      id: 2 },
    { value: 'PRO',        label: 'Pro',        id: 3 },
    { value: 'ENTERPRISE', label: 'Enterprise', id: 4 },
  ];

  constructor(
    public dialogRef: MatDialogRef<CreateTemplateDialogComponent, CreateTemplateResult>,
    private formTemplateService: FormTemplateService,
    private adminService: AdminService,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: CreateTemplateDialogData | null,
  ) {
    // Der JSON-Validator hängt an der Quelle; deshalb erst hier, wo `form` steht.
    this.jsonControl.addValidators(control => this.validateJson(control));
  }

  ngOnInit(): void {
    this.orgsLoading = true;
    this.adminService.getAllOrganisationsEndpoint().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (orgs) => {
        this.allOrgs = orgs.filter(o => !o.deleted).sort((a, b) =>
          (a.displayName ?? '').localeCompare(b.displayName ?? '')
        );
        this.filteredOrgs = this.allOrgs;
        this.orgsLoading = false;
        this.preselectOrg();
        this.cdr.markForCheck();
      },
      error: () => {
        this.orgsLoading = false;
        this.cdr.markForCheck();
      }
    });

    this.orgSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(150),
      takeUntil(this.destroy$),
    ).subscribe(value => {
      const search = typeof value === 'string' ? value.toLowerCase() : '';
      this.filteredOrgs = search
        ? this.allOrgs.filter(o =>
            (o.displayName ?? '').toLowerCase().includes(search) ||
            (o.name ?? '').toLowerCase().includes(search)
          )
        : this.allOrgs;
      // Clear selection when user types instead of picking from list
      if (typeof value === 'string' && this.selectedOrg) {
        this.selectedOrg = null;
      }
      this.cdr.markForCheck();
    });

    this.form.controls.source.valueChanges.pipe(
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.jsonControl.updateValueAndValidity();
      this.saveError = null;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get jsonControl(): FormControl<string> {
    return this.form.controls.json;
  }

  get isForOrg(): boolean {
    return this.selectedOrg !== null;
  }

  get isJsonSource(): boolean {
    return this.form.controls.source.value === 'json';
  }

  displayOrg(org: OrganisationSummaryDto | string | null): string {
    if (!org) return '';
    if (typeof org === 'string') return org;
    return org.displayName ?? org.name ?? '';
  }

  onOrgSelected(org: OrganisationSummaryDto): void {
    this.selectedOrg = org;
    this.cdr.markForCheck();
  }

  /**
   * Aus der Org-Detailseite geöffnet: diese Org gleich eintragen. Das Objekt
   * (nicht der Name) geht ins Suchfeld, damit valueChanges die Auswahl nicht
   * wieder löscht. Findet sich die Org nicht (gelöscht), bleibt das Feld leer.
   */
  private preselectOrg(): void {
    const org = this.allOrgs.find(o => o.id === this.data?.orgId);
    if (!org) return;
    this.selectedOrg = org;
    this.orgSearchControl.setValue(org);
  }

  clearOrg(): void {
    this.selectedOrg = null;
    this.orgSearchControl.setValue('');
    this.cdr.markForCheck();
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Dieselbe Datei soll nach einer Korrektur erneut gewählt werden können.
    input.value = '';
    if (!file) return;

    this.loadedFileName = file.name;
    this.jsonControl.setValue(await file.text());
    this.jsonControl.markAsTouched();
    this.cdr.markForCheck();
  }

  save(): void {
    this.saveError = null;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const name = this.form.controls.name.value.trim();
    const description = this.form.controls.description.value.trim();
    const openDesigner = !this.isJsonSource;

    let auditFormJson: FormJson;
    if (this.isJsonSource) {
      const parsed = parseFormJsonInput(this.jsonControl.value);
      if (!parsed.ok) {
        // Der Validator hat das schon gemeldet; nur zur Sicherheit.
        this.jsonControl.markAsTouched();
        this.cdr.markForCheck();
        return;
      }
      auditFormJson = parsed.form;
    } else {
      auditFormJson = createEmptyAuditFormJson(name, description);
    }

    this.saving = true;
    this.cdr.markForCheck();

    let request$: Observable<FormTemplateDto>;
    if (this.isForOrg && this.selectedOrg?.id) {
      request$ = this.adminService.createOrgFormTemplateEndpoint(this.selectedOrg.id, {
        name,
        description: description || null,
        auditFormJson,
        freezeFormOnAuditCreate: true,
      });
    } else {
      const minimalPlanOption = this.planOptions.find(p => p.value === this.form.controls.minimalPlanKey.value);
      request$ = this.formTemplateService.postFormTemplateEndpoint({
        name,
        description: description || null,
        auditFormJson,
        entryFormJson: null,
        isSystemTemplate: true,
        catalogKey: this.form.controls.catalogKey.value || null,
        minimalSubscriptionId: minimalPlanOption?.id ?? null,
      });
    }

    request$.subscribe({
      next: (template) => {
        this.saving = false;
        this.dialogRef.close({ id: template.id!, openDesigner });
      },
      error: (err: unknown) => this.onSaveError(err),
    });
  }

  /**
   * Strukturfehler der Server-Validierung betreffen das Formular. Kam es aus
   * dem JSON-Feld, stehen sie dort; sonst oben im Dialog (DOCU-663).
   */
  private onSaveError(err: unknown): void {
    this.saving = false;
    const message = extractApiErrorMessage(err, 'Speichern fehlgeschlagen', false);
    const isValidation = err instanceof HttpErrorResponse && err.status === 400;

    if (isValidation && this.isJsonSource) {
      this.jsonControl.setErrors({ server: message });
      this.jsonControl.markAsTouched();
    } else {
      this.saveError = message;
    }
    this.cdr.markForCheck();
  }

  private validateJson(control: AbstractControl): ValidationErrors | null {
    if (!this.isJsonSource) return null;
    const result = parseFormJsonInput(control.value ?? '');
    return result.ok ? null : { json: result.error };
  }
}

function notBlank(control: AbstractControl): ValidationErrors | null {
  return typeof control.value === 'string' && !control.value.trim() ? { required: true } : null;
}
